import { useEffect, useState } from "react";
import { Group, Loader } from "@mantine/core";
import ContractCrewsPage from "./contractCrews/ContractCrewsPage";
import ContractMachinesPage from "./contractMachines/ContractMachinesPage";
import ContractPriceGroupsPage from "./contractPriceGroups/ContractPriceGroupsPage";
import ContractSidecostGroupsPage from "./contractSidecostGroups/ContractSidecostGroupsPage";
import DeputiesPage from "./deputies/DeputiesPage";
import GeneralPriceListPage from "./generalPriceList/GeneralPriceListPage";
import GeneralSidecostGroupsPage from "./generalSidecostGroups/GeneralSidecostGroupsPage";
import SubscribersPage from "./subscribers/SubscribersPage";
import UserDefaultSettingsPage from "./userDefaults/UserDefaultSettingsPage";
import SettingsLanding from "./landing/SettingsLanding";
import SettingsPlaceholderView from "./SettingsPlaceholderView";
import SettingsPopupWindow, {
  useSettingsPopupWindow,
} from "./SettingsPopupWindow";
import { useSettingsPrefetch } from "./hooks/useSettingsPrefetch";
import { settingsLinks, SettingsViewId } from "./settingsLandingData";
import { useSettingsPageAccess } from "./useSettingsPageAccess";
import useTransientStore from "../../data/transcientStore";
import { usePermissions } from "../../hooks/usePermissions";

export default function SettingsPanel() {
  const selectedAddressSystem =
    useTransientStore((state) => state.selectedAddressSystem);
  const selectedAddressSystemId = selectedAddressSystem?.id ?? 0;
  const { checkAccess } = usePermissions();
  const { isViewVisible } = useSettingsPageAccess();
  const popupWindow = useSettingsPopupWindow();
  const {
    prefetchedPriceList,
    setPrefetchedPriceList,
    prefetchedSubscribers,
    setPrefetchedSubscribers,
    prefetchedDeputies,
    setPrefetchedDeputies,
    prefetchedContractPriceGroups,
    setPrefetchedContractPriceGroups,
    prefetchedContractSidecostGroups,
    setPrefetchedContractSidecostGroups,
    prefetchedGeneralSidecostGroups,
    setPrefetchedGeneralSidecostGroups,
    prefetchedContractCrews,
    setPrefetchedContractCrews,
    prefetchedContractMachines,
    setPrefetchedContractMachines,
    resetContractScopedPrefetch,
  } = useSettingsPrefetch(selectedAddressSystemId, checkAccess, isViewVisible);
  const [activeView, setActiveView] = useState<SettingsViewId | null>(null);
  const [priceListMounted, setPriceListMounted] = useState(false);
  const [contractPriceGroupsMounted, setContractPriceGroupsMounted] =
    useState(false);
  const [contractSidecostGroupsMounted, setContractSidecostGroupsMounted] =
    useState(false);
  const [generalSidecostGroupsMounted, setGeneralSidecostGroupsMounted] =
    useState(false);
  const [contractCrewsMounted, setContractCrewsMounted] = useState(false);
  const [contractMachinesMounted, setContractMachinesMounted] = useState(false);
  const [subscribersMounted, setSubscribersMounted] = useState(false);
  const [deputiesMounted, setDeputiesMounted] = useState(false);
  const [userDefaultsMounted, setUserDefaultsMounted] = useState(false);

  const resetPopupMounts = () => {
    setPriceListMounted(false);
    setContractPriceGroupsMounted(false);
    setContractSidecostGroupsMounted(false);
    setGeneralSidecostGroupsMounted(false);
    setContractCrewsMounted(false);
    setContractMachinesMounted(false);
    setDeputiesMounted(false);
  };

  const openView = (view: SettingsViewId) => {
    if (!isViewVisible(view)) {
      setActiveView(null);
      return;
    }

    if (view === "unit-price-list-lines") {
      setPriceListMounted(false);
      setPrefetchedPriceList(null);  // Clear cached data to force fresh fetch
    }
    if (view === "subscribers") {
      setSubscribersMounted(false);
    }
    if (view === "deputies") {
      setDeputiesMounted(false);
    }
    if (view === "user-defaults") {
      setUserDefaultsMounted(false);
    }
    resetPopupMounts();
    setActiveView(view);
  };

  const closeView = () => {
    resetContractScopedPrefetch(activeView);
    setPriceListMounted(false);
    setPrefetchedPriceList(null);  // Clear cached price list data
    setSubscribersMounted(false);
    setDeputiesMounted(false);
    setUserDefaultsMounted(false);
    resetPopupMounts();
    setActiveView(null);
  };

  const getPopupView = () => {
    if (activeView === "unit-price-list-lines") {
      return {
        title: "Unit price list lines",
        mounted: priceListMounted,
        content: (
          <GeneralPriceListPage
            onBack={closeView}
            initialData={prefetchedPriceList}
            onDataLoaded={setPrefetchedPriceList}
          />
        ),
      };
    }

    if (activeView === "unit-prices-for-contracts") {
      return {
        title: "Unit prices for contracts",
        mounted: contractPriceGroupsMounted,
        content: (
          <ContractPriceGroupsPage
            onBack={closeView}
            initialData={prefetchedContractPriceGroups}
            onDataLoaded={setPrefetchedContractPriceGroups}
          />
        ),
      };
    }

    if (activeView === "contract-sidecost-groups") {
      return {
        title: "Contract indirect costs",
        mounted: contractSidecostGroupsMounted,
        content: (
          <ContractSidecostGroupsPage
            onBack={closeView}
            initialData={prefetchedContractSidecostGroups}
            onDataLoaded={setPrefetchedContractSidecostGroups}
          />
        ),
      };
    }

    if (activeView === "general-sidecost-groups") {
      return {
        title: "General indirect cost groups",
        mounted: generalSidecostGroupsMounted,
        content: (
          <GeneralSidecostGroupsPage
            onBack={closeView}
            initialData={prefetchedGeneralSidecostGroups}
            onDataLoaded={setPrefetchedGeneralSidecostGroups}
          />
        ),
      };
    }

    if (activeView === "contract-crews") {
      return {
        title: "Working groups (crews)",
        mounted: contractCrewsMounted,
        content: (
          <ContractCrewsPage
            onBack={closeView}
            initialData={prefetchedContractCrews}
            onDataLoaded={setPrefetchedContractCrews}
          />
        ),
      };
    }

    if (activeView === "deputies") {
      return {
        title: "Deputies",
        mounted: deputiesMounted,
        content: (
          <DeputiesPage
            onBack={closeView}
            initialData={prefetchedDeputies}
            onDataLoaded={setPrefetchedDeputies}
          />
        ),
      };
    }

    if (activeView === "contract-machines") {
      return {
        title: "Equipment (machines)",
        mounted: contractMachinesMounted,
        content: (
          <ContractMachinesPage
            onBack={closeView}
            initialData={prefetchedContractMachines}
            onDataLoaded={setPrefetchedContractMachines}
          />
        ),
      };
    }

    if (activeView === "user-defaults") {
      return {
        title: "User Default Settings",
        mounted: userDefaultsMounted,
        content: <UserDefaultSettingsPage onBack={closeView} />,
      };
    }

    return null;
  };

  useEffect(() => {
    if (
      activeView !== "unit-price-list-lines" &&
      activeView !== "unit-prices-for-contracts" &&
      activeView !== "contract-sidecost-groups" &&
      activeView !== "general-sidecost-groups" &&
      activeView !== "contract-crews" &&
      activeView !== "deputies" &&
      activeView !== "contract-machines" &&
      activeView !== "user-defaults"
    ) {
      return undefined;
    }

    const frameId = globalThis.requestAnimationFrame(() => {
      setPriceListMounted(activeView === "unit-price-list-lines");
      setContractPriceGroupsMounted(activeView === "unit-prices-for-contracts");
      setContractSidecostGroupsMounted(activeView === "contract-sidecost-groups");
      setGeneralSidecostGroupsMounted(activeView === "general-sidecost-groups");
      setContractCrewsMounted(activeView === "contract-crews");
      setDeputiesMounted(activeView === "deputies");
      setContractMachinesMounted(activeView === "contract-machines");
      setUserDefaultsMounted(activeView === "user-defaults");
    });
    return () => globalThis.cancelAnimationFrame(frameId);
  }, [activeView]);

  useEffect(() => {
    if (activeView !== "subscribers") return undefined;

    const frameId = globalThis.requestAnimationFrame(() => {
      setSubscribersMounted(true);
    });
    return () => globalThis.cancelAnimationFrame(frameId);
  }, [activeView]);

  useEffect(() => {
    if (activeView && !isViewVisible(activeView)) {
      setPriceListMounted(false);
      setUserDefaultsMounted(false);
      setSubscribersMounted(false);
      setDeputiesMounted(false);
      resetPopupMounts();
      setActiveView(null);
    }
  }, [activeView, isViewVisible]);

  const popupView = getPopupView();

  if (popupView) {
    return (
      <>
        <SettingsLanding onOpenView={openView} />
        <SettingsPopupWindow
          title={popupView.title}
          {...popupWindow}
          onClose={closeView}
        >
          {popupView.mounted ? (
            popupView.content
          ) : (
            <Group justify="center" h="100%">
              <Loader size="sm" />
            </Group>
          )}
        </SettingsPopupWindow>
      </>
    );
  }

  if (activeView === "subscribers") {
    return (
      <>
        <SettingsLanding onOpenView={openView} />
        <SettingsPopupWindow
          title="Subscribers"
          {...popupWindow}
          onClose={closeView}
        >
          {subscribersMounted ? (
            <SubscribersPage
              onBack={closeView}
              initialData={prefetchedSubscribers}
              onDataLoaded={setPrefetchedSubscribers}
            />
          ) : (
            <Group justify="center" h="100%">
              <Loader size="sm" />
            </Group>
          )}
        </SettingsPopupWindow>
      </>
    );
  }

  if (activeView) {
    const link = settingsLinks.find((item) => item.id === activeView);
    return (
      <SettingsPlaceholderView
        onBack={() => setActiveView(null)}
        titleTranslationKey={link?.translationKey ?? ""}
        defaultTitle={link?.defaultLabel ?? "Settings"}
      />
    );
  }

  return <SettingsLanding onOpenView={openView} />;
}
